import { useState } from 'react';
import { api } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

const KINDS = [
  { id: 'items', title: 'Items', text: 'Your product list with prices, costs and stock levels. New items are added, existing ones updated by barcode.' },
  { id: 'prices', title: 'Price changes', text: 'Just a barcode and a new price for items you already carry.' },
  { id: 'suppliers', title: 'Suppliers', text: 'Distributors and wholesalers: name, license number, email.' },
  { id: 'supplier-items', title: 'Supplier item codes', text: "Each supplier's own number for a product, so invoices match your items on their own." },
  { id: 'customers', title: 'Customers', text: 'Names, phone, email, club membership, opening points and group.' },
  { id: 'price-list', title: 'Price list', text: 'Prices for one price list. Pick the list below first.' },
] as const;

async function download(path: string, fallback: string) {
  const res = await api<Response>(path, { raw: true });
  if (!res.ok) throw new Error(`Could not download (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = fallback; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Import and export spreadsheets (CSV). Every import shows a preview first; nothing changes until you apply it.
export default function DataIO() {
  const lists = useLoad(() => api<any[]>('/api/price-lists'), []);
  const [listId, setListId] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Import and export</h1>
        <p>Move data in and out with a CSV, Excel (.xlsx) or PDF file. Download the template to see the columns. When you import, you see exactly what would happen first, and a row with a problem is skipped without stopping the rest.</p></div></div>
      <ErrorNote error={err} />
      <MasterCard onError={setErr} />
      {KINDS.map((k) => (
        <Card key={k.id} kind={k.id} title={k.title} text={k.text} listId={listId} onError={setErr}
          extra={k.id === 'price-list' ? (
            <label>Price list<select value={listId} onChange={(e) => setListId(e.target.value)}><option value="">Choose a list</option>{lists.data?.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
          ) : null} />
      ))}
    </div>
  );
}

const toBase64 = async (f: File) => {
  const bytes = new Uint8Array(await f.arrayBuffer()); let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};
const FIELD_NAMES: Record<string, string> = { upc: 'UPC', itemCode: 'item no.', brand: 'brand', name: 'description', size: 'size', packSize: 'pack/size', pack: 'case pack', category: 'type', unitCost: 'bottle cost', caseCost: 'case cost', price: 'shelf price', distributor: 'distributor' };

// Many distributor price sheets at once, whatever their layout: columns are found by their header, each UPC becomes one item.
function MasterCard({ onError }: { onError: (e: unknown) => void }) {
  const [files, setFiles] = useState<{ name: string; data: string; distributor: string }[]>([]);
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const run = async (apply: boolean) => {
    setBusy(true); onError(null);
    try { setRes(await api('/api/data/item-master', { body: { apply, files: files.map((f) => ({ ...f, distributor: f.distributor || null })) } })); }
    catch (e) { onError(e); } finally { setBusy(false); }
  };
  const problems = res ? res.files.flatMap((f: any) => f.problems) : [];
  return (
    <div className="panel"><div className="panel-body stack">
      <div><h2 style={{ margin: 0 }}>Distributor price sheets (item master)</h2>
        <p className="faint" style={{ margin: 0 }}>Choose every distributor file at once (CSV or Excel), in any layout. Columns are found by their names, the same UPC from several files becomes one item with the lowest bottle cost, and items without a shelf price go into the catalog without being put on your shelf.</p></div>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <label className="button" style={{ cursor: 'pointer' }}>
          Choose files…
          <input type="file" multiple accept=".csv,.tsv,.txt,.xlsx,text/csv" hidden onChange={async (e) => {
            const list = Array.from(e.target.files ?? []); e.target.value = '';
            setRes(null); onError(null);
            try { setFiles(await Promise.all(list.map(async (f) => ({ name: f.name, data: await toBase64(f), distributor: '' })))); } catch (err) { onError(err); }
          }} />
        </label>
        {files.length > 0 && <span className="faint">{files.length} file{files.length === 1 ? '' : 's'}</span>}
      </div>
      {files.length > 0 && (
        <div className="table-wrap"><table className="grid">
          <thead><tr><th>File</th><th>Distributor (if the file has no distributor column)</th></tr></thead>
          <tbody>{files.map((f, i) => (
            <tr key={f.name + i}><td>{f.name}</td><td><input value={f.distributor} placeholder="Name or license number" onChange={(e) => setFiles(files.map((x, j) => (j === i ? { ...x, distributor: e.target.value } : x)))} /></td></tr>))}</tbody>
        </table></div>
      )}
      {files.length > 0 && (
        <div className="row" style={{ gap: 8 }}>
          <button disabled={busy} onClick={() => run(false)}>{busy ? 'Reading…' : 'Preview'}</button>
          <button className="primary" disabled={busy || !res || res.import.applied || res.import.errors === res.import.total} onClick={() => run(true)}>Apply import</button>
        </div>
      )}
      {res && (
        <div className="stack">
          <div className={`notice ${res.import.applied ? 'good' : ''}`}>
            {res.import.applied ? 'Done. ' : 'Preview, nothing has changed yet. '}
            {res.lines} lines in the files became {res.unique} items ({res.duplicates} repeats put together) · {res.import.created} new · {res.import.updated} updated · {res.import.skipped} unchanged · {res.import.errors} with problems
          </div>
          <div className="table-wrap"><table className="grid">
            <thead><tr><th>File and sheet</th><th className="n">Header row</th><th>Columns found</th><th className="n">Items</th><th className="n">Lines skipped</th></tr></thead>
            <tbody>{res.files.flatMap((f: any) => f.sheets.map((s: any) => (
              <tr key={f.file + s.sheet}><td>{f.file}{s.sheet !== f.file.replace(/\.[a-z]+$/i, '') ? ` · ${s.sheet}` : ''}</td><td className="n">{s.headerRow}</td>
                <td className="faint">{Object.entries(s.columns).map(([k, v]) => `${FIELD_NAMES[k] ?? k} = “${v}”`).join(', ')}</td><td className="n">{s.lines}</td><td className="n">{s.problems}</td></tr>)))}</tbody>
          </table></div>
          {problems.length > 0 && (
            <details><summary>{problems.length} line{problems.length === 1 ? '' : 's'} could not be read</summary>
              <ul>{problems.slice(0, 300).map((p: any, i: number) => <li key={i}>{p.file}{p.sheet ? ` · ${p.sheet}` : ''}{p.row ? ` row ${p.row}` : ''}: {p.message}</li>)}</ul></details>
          )}
          {res.conflicts.length > 0 && (
            <details><summary>{res.conflicts.length} item{res.conflicts.length === 1 ? '' : 's'} where the files disagree</summary>
              <ul>{res.conflicts.map((c: any, i: number) => <li key={i}>{c.label}: {c.message}</li>)}</ul></details>
          )}
          <div className="table-wrap"><table className="grid">
            <thead><tr><th>Item</th><th>Result</th></tr></thead>
            <tbody>{res.import.results.map((r: any) => (
              <tr key={r.row}><td>{r.label}</td>
                <td><span className={`chip ${r.action === 'error' ? 'bad' : r.action === 'skip' ? '' : 'good'}`}>{({ create: 'new', update: 'update', skip: 'same', error: 'problem' } as any)[r.action]}</span> {r.message}</td></tr>))}</tbody>
          </table></div>
          {res.import.total > res.import.results.length && <div className="faint">Showing {res.import.results.length} of {res.import.total} items. All problems are listed.</div>}
        </div>
      )}
    </div></div>
  );
}

function Card({ kind, title, text, listId, extra, onError }: { kind: string; title: string; text: string; listId: string; extra: React.ReactNode; onError: (e: unknown) => void }) {
  const [csv, setCsv] = useState<{ name: string; text: string; note?: string } | null>(null);
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const needList = kind === 'price-list' && !listId;
  const run = async (apply: boolean) => {
    if (!csv) return;
    setBusy(true); onError(null);
    try { setRes(await api('/api/data/import/' + kind, { body: { csv: csv.text, apply, ...(kind === 'price-list' ? { priceListId: listId } : {}) } })); }
    catch (e) { onError(e); } finally { setBusy(false); }
  };
  const grab = (fn: () => Promise<void>) => fn().catch(onError);
  return (
    <div className="panel"><div className="panel-body stack">
      <div className="row"><div className="grow"><h2 style={{ margin: 0 }}>{title}</h2><p className="faint" style={{ margin: 0 }}>{text}</p></div></div>
      {extra}
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button onClick={() => grab(() => download(`/api/data/template/${kind}`, `${kind}-template.csv`))}>Download template</button>
        <button disabled={needList} onClick={() => grab(() => download(`/api/data/export/${kind}${kind === 'price-list' ? `?priceListId=${listId}` : ''}`, `${kind}.csv`))}>Export current</button>
        <label className="button" style={{ cursor: 'pointer' }}>
          Choose a file…
          <input type="file" accept=".csv,.tsv,.txt,.xlsx,.pdf,text/csv,application/pdf" hidden onChange={async (e) => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return;
            setRes(null); onError(null);
            if (/\.(xlsx|pdf)$/i.test(f.name)) {
              setBusy(true);
              try {
                const bytes = new Uint8Array(await f.arrayBuffer()); let bin = '';
                for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                const r = await api<{ csv: string; source: string }>('/api/data/convert/' + kind, { body: { name: f.name, data: btoa(bin) } });
                setCsv({ name: f.name, text: r.csv, note: r.source === 'pdf' ? 'Read from the PDF by the AI helper. Check the preview carefully before you apply it.' : undefined });
              } catch (err) { onError(err); } finally { setBusy(false); }
            } else setCsv({ name: f.name, text: await f.text() });
          }} />
        </label>
        {csv && <span className="faint">{csv.name}</span>}
        {busy && !csv && <span className="faint">Reading the file…</span>}
      </div>
      {csv?.note && <div className="notice">{csv.note}</div>}
      {csv && (
        <div className="row" style={{ gap: 8 }}>
          <button disabled={busy || needList} onClick={() => run(false)}>Preview</button>
          <button className="primary" disabled={busy || needList || !res || res.applied || res.errors === res.total} onClick={() => run(true)}>Apply import</button>
        </div>
      )}
      {res && (
        <div className="stack">
          <div className={`notice ${res.applied ? 'good' : ''}`}>
            {res.applied ? 'Done. ' : 'Preview, nothing has changed yet. '}
            {res.created} new · {res.updated} updated · {res.skipped} unchanged · {res.errors} with problems
          </div>
          <div className="table-wrap"><table className="grid">
            <thead><tr><th className="n">Row</th><th>What</th><th>Result</th></tr></thead>
            <tbody>{res.results.map((r: any) => (
              <tr key={r.row}><td className="n">{r.row}</td><td>{r.label}</td>
                <td><span className={`chip ${r.action === 'error' ? 'bad' : r.action === 'skip' ? '' : 'good'}`}>{({ create: 'new', update: 'update', skip: 'same', error: 'problem' } as any)[r.action]}</span> {r.message}</td></tr>))}</tbody>
          </table></div>
          {res.total > res.results.length && <div className="faint">Showing the first {res.results.length} of {res.total} rows. All problems are listed.</div>}
        </div>
      )}
    </div></div>
  );
}
